import { Badge } from '@/types'
import { BadgeDisplay } from './BadgeDisplay'

interface BadgeWidgetProps {
  badges: Badge[]
  maxItems?: number
  title?: string
  showViewAll?: boolean
  className?: string
}

export function BadgeWidget({ 
  badges, 
  maxItems = 3,
  title = "Recent Badges",
  showViewAll = true,
  className = '' 
}: BadgeWidgetProps) {
  // Filter to earned badges only and sort by earned date (newest first)
  const earnedBadges = badges
    .filter(badge => badge.earnedAt)
    .sort((a, b) => new Date(b.earnedAt!).getTime() - new Date(a.earnedAt!).getTime())
    .slice(0, maxItems)

  const totalEarned = badges.filter(badge => badge.earnedAt).length
  const totalBadges = badges.length

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${className}`}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {totalEarned} of {totalBadges} badges earned
            </p>
          </div>
          
          {/* Badge icon */}
          <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
            <span className="text-yellow-600 dark:text-yellow-400 text-xl">🏆</span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-600 dark:text-gray-400">Progress</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {Math.round((totalEarned / totalBadges) * 100)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-yellow-400 to-yellow-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${(totalEarned / totalBadges) * 100}%` }}
            />
          </div>
        </div>

        {/* Recent badges */}
        {earnedBadges.length === 0 ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-gray-400 dark:text-gray-500 text-xl">🏅</span>
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              No badges earned yet
            </p>
            <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">
              Keep improving to earn your first badge!
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {earnedBadges.map((badge) => (
              <BadgeDisplay
                key={badge.id}
                badge={badge}
                size="sm"
                showDescription={false}
                showEarnedDate={true}
              />
            ))}
          </div>
        )}

        {/* View all link */}
        {showViewAll && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <a
              href="/badges"
              className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium flex items-center justify-center"
            >
              View all badges →
            </a>
          </div>
        )}
      </div>
    </div>
  )
}